//
// # HierarchyContextMenu
//
// Shared right-click context menu for hierarchy tree rows and search result rows.
// Renders as a portal to document.body, dismisses on outside click or Escape.
//

import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ExternalLink, Maximize } from "lucide-react";

export interface HierarchyContextMenuState {
  readonly x: number;
  readonly y: number;
}

export function HierarchyContextMenu({
  menu,
  onClose,
  onOpenInNewTab,
  canOpenInNewTab,
  onZoomTo,
  canZoomTo,
}: {
  readonly menu: HierarchyContextMenuState;
  readonly onClose: () => void;
  readonly onOpenInNewTab: () => void;
  readonly canOpenInNewTab: boolean;
  readonly onZoomTo: () => void;
  readonly canZoomTo: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={ref}
      className="fixed z-50 py-1 rounded shadow-lg border text-xs min-w-[140px]"
      style={{
        left: menu.x,
        top: menu.y,
        backgroundColor: "var(--color-ide-surface)",
        borderColor: "var(--color-ide-border)",
        color: "var(--color-ide-text)",
      }}
    >
      <button
        className="flex items-center gap-2 w-full px-3 py-1.5 text-left hover:bg-white/10"
        style={
          canOpenInNewTab
            ? { color: "var(--color-ide-text)" }
            : {
                color: "var(--color-ide-text-dim)",
                opacity: 0.5,
                cursor: "default",
              }
        }
        onClick={() => {
          if (canOpenInNewTab) {
            onOpenInNewTab();
            onClose();
          }
        }}
      >
        <ExternalLink size={12} />
        Open in New Tab
      </button>
      <button
        className="flex items-center gap-2 w-full px-3 py-1.5 text-left hover:bg-white/10"
        style={
          canZoomTo
            ? { color: "var(--color-ide-text)" }
            : {
                color: "var(--color-ide-text-dim)",
                opacity: 0.5,
                cursor: "default",
              }
        }
        onClick={() => {
          if (canZoomTo) {
            onZoomTo();
            onClose();
          }
        }}
      >
        <Maximize size={12} />
        Zoom To
      </button>
    </div>,
    document.body
  );
}
